import { useHistory } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Alert,
    Button,
    Spinner,
    Table,
    Pagination,
    Header,
    SpaceBetween,
    PropertyFilter,
    PropertyFilterProps,
} from '@amzn/awsui-components-react-v3';
import {
    AJAX_CALL_ERROR,
    GRIMSBY_PAGE_COUNT,
    LOADING_TEXT,
} from '../../../../common/constants/grimsby';
import {
    resetCoursesListSlice,
    getCoursesList,
    selectIsLoading,
    selectIsLoaded,
    selectAllCourses,
    selectError,
    selectCurrentPageIndex,
    selectTotalCoursesCount,
    selectPagesCount,
    setSearchText,
    setFrom,
    setActive,
    setCurrentPageIndex,
} from '../../../../common/store/slices/courseListSlice';
import Can from '../../../../common/components/Can';
import { Actions } from '../../../../common/constants/auth';
import { createPropertyFilterI18nStrings } from '../../../../common/utils/createPropertyFilterI18nStrings';
import {
    STATUS_GROUP_VALUES_LABEL,
    STATUS_PROPERTY_KEY,
    STATUS_PROPERTY_LABEL,
    STATUS_VALUE_ACTIVE,
    STATUS_VALUE_INACTIVE,
    tokensToStatusFilters,
} from '../../../../common/utils/statusFilter';
import { columnDefinitions } from './AdminCoursesList.definitions';

export const USER_TABLE_FILTER_TEXT = 'Search courses by title or property';
const COURSES_TABLE_TITLE = 'Courses';
export const COURSES_NAME_KEY = 'course';

/**
 * Property-filter definition for the Courses list, structured like the
 * Instructor and Learning Activity lists: the filters are baked into the
 * search input as property tokens (plus free-text search) rather than a
 * separate dropdown control.
 *
 * The Status property / `active` mapping is shared with the other admin
 * business-data lists (see common/utils/statusFilter).
 */
export const COURSE_FILTERING_PROPERTIES: ReadonlyArray<PropertyFilterProps.FilteringProperty> =
    [
        {
            key: STATUS_PROPERTY_KEY,
            operators: ['='],
            propertyLabel: STATUS_PROPERTY_LABEL,
            groupValuesLabel: STATUS_GROUP_VALUES_LABEL,
        },
    ];

export const COURSE_FILTERING_OPTIONS: ReadonlyArray<PropertyFilterProps.FilteringOption> =
    [
        {
            propertyKey: STATUS_PROPERTY_KEY,
            value: STATUS_VALUE_ACTIVE,
        },
        {
            propertyKey: STATUS_PROPERTY_KEY,
            value: STATUS_VALUE_INACTIVE,
        },
    ];

/**
 * Translate a PropertyFilter query into the courses list filter state via the
 * shared status-filter mapping. The v3 token shape (propertyKey?: string,
 * value) satisfies the shared StatusFilterToken contract directly.
 */
const queryToCourseFilters = (query: PropertyFilterProps.Query) =>
    tokensToStatusFilters(query.tokens);

const AdminCoursesList = () => {
    const error = useSelector(selectError);
    const isLoading = useSelector(selectIsLoading);
    const isLoaded = useSelector(selectIsLoaded);
    const courseList = useSelector(selectAllCourses);
    const pagesCount = useSelector(selectPagesCount);
    const totalCoursesCount = useSelector(selectTotalCoursesCount);
    const currentPageIndex = useSelector(selectCurrentPageIndex);
    const [query, setQuery] = useState<PropertyFilterProps.Query>({
        // Default the list to Active courses, with the Status token shown in the
        // search bar so the applied filter is visible. Removing the token (no
        // status filtering) shows all courses.
        tokens: [
            {
                propertyKey: STATUS_PROPERTY_KEY,
                operator: '=',
                value: STATUS_VALUE_ACTIVE,
            },
        ],
        operation: 'and',
    });
    const history = useHistory();
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(getCoursesList());
        return () => {
            dispatch(resetCoursesListSlice());
        };
    }, [dispatch]);

    function handlePropertyFilterChange(
        updatedQuery: PropertyFilterProps.Query,
    ) {
        setQuery(updatedQuery);
        const { searchText, active } = queryToCourseFilters(updatedQuery);
        dispatch(setSearchText(searchText));
        dispatch(setActive(active));
        dispatch(setCurrentPageIndex(1));
        dispatch(setFrom(0));
        dispatch(getCoursesList());
    }

    if (error) {
        return (
            <Alert
                data-testid="AdminCoursesListError"
                header="Error"
                type="error"
            >
                {AJAX_CALL_ERROR}
            </Alert>
        );
    } else if (isLoaded) {
        return (
            <Table
                data-testid="AdminCoursesList"
                loading={isLoading}
                loadingText={LOADING_TEXT}
                columnDefinitions={columnDefinitions}
                items={courseList}
                stickyHeader={true}
                stickyColumns={{
                    first: 1,
                    last: 0,
                }}
                header={
                    <Header
                        variant="h1"
                        actions={
                            <Can
                                perform={Actions.SYSTEM_ADMINISTRATION}
                                yes={() => (
                                    <SpaceBetween
                                        direction="horizontal"
                                        size="xs"
                                    >
                                        <Button
                                            iconName="add-plus"
                                            variant="primary"
                                            data-testid="AdminCoursesListActionsCreate"
                                            onClick={() =>
                                                history.push({
                                                    pathname: `/admin/business-data/courses/create`,
                                                })
                                            }
                                        >
                                            Add new course
                                        </Button>
                                    </SpaceBetween>
                                )}
                            />
                        }
                    >
                        {`${COURSES_TABLE_TITLE} (${totalCoursesCount})`}
                    </Header>
                }
                empty={
                    <div className="awsui-util-t-c">
                        <div className="awsui-util-pt-s awsui-util-mb-xs">
                            <b>No courses</b>
                        </div>
                        <p className="awsui-util-mb-s">
                            No courses to display.
                        </p>
                    </div>
                }
                filter={
                    <PropertyFilter
                        data-testid="AdminCoursesListPropertyFilter"
                        query={query}
                        onChange={({ detail }) =>
                            handlePropertyFilterChange(detail)
                        }
                        hideOperations={true}
                        filteringProperties={[...COURSE_FILTERING_PROPERTIES]}
                        filteringOptions={[...COURSE_FILTERING_OPTIONS]}
                        filteringAriaLabel={USER_TABLE_FILTER_TEXT}
                        filteringPlaceholder={USER_TABLE_FILTER_TEXT}
                        i18nStrings={createPropertyFilterI18nStrings({
                            filteringAriaLabel: USER_TABLE_FILTER_TEXT,
                            filteringPlaceholder: USER_TABLE_FILTER_TEXT,
                        })}
                    />
                }
                pagination={
                    <Pagination
                        pagesCount={pagesCount}
                        disabled={isLoading}
                        currentPageIndex={currentPageIndex}
                        onChange={(event) => {
                            dispatch(
                                setCurrentPageIndex(
                                    event.detail.currentPageIndex,
                                ),
                            );
                            const from =
                                (event.detail.currentPageIndex - 1) *
                                GRIMSBY_PAGE_COUNT;
                            dispatch(setFrom(from));
                            dispatch(getCoursesList());
                        }}
                    />
                }
            ></Table>
        );
    } else {
        return <Spinner data-testid="AdminCoursesListSpinner" />;
    }
};

export default AdminCoursesList;
